// Status IDs for different entity types
// These should match the IDs in the statuses table

export const STATUS_IDS = {
  // User statuses
  USER_ACTIVE: 1,
  USER_INACTIVE: 2,
  USER_ARCHIVED: 3,

  // Venue statuses
  VENUE_ACTIVE: 4,
  VENUE_INACTIVE: 5,
  VENUE_MAINTENANCE: 6,
  VENUE_ARCHIVED: 7,

  // Studio statuses
  STUDIO_ACTIVE: 8,
  STUDIO_INACTIVE: 9,
  STUDIO_MAINTENANCE: 10,
  STUDIO_ARCHIVED: 11,

  // Show statuses
  SHOW_ACTIVE: 12,
  SHOW_INACTIVE: 13,
  SHOW_ARCHIVED: 14,

  // Booking statuses
  BOOKING_PRELIMINARY: 15, // Preliminary booking status
  BOOKING_CONFIRMED: 16, // Confirmed/reserved booking status
  BOOKING_CANCELLED: 17,
  BOOKING_COMPLETED: 18,
  BOOKING_NO_SHOW: 19,
  BOOKING_ARCHIVED: 43,
  BOOKING_DRAFT: 46, // Draft booking status

  // Payment statuses
  PAYMENT_PENDING: 20,
  PAYMENT_PAID: 21,
  PAYMENT_FAILED: 22,
  PAYMENT_REFUNDED: 23,
  PAYMENT_PARTIAL: 24,
  PAYMENT_INVOICED: 55, // Invoice generated but not yet paid

  // Price modifier statuses
  PRICE_MODIFIER_ACTIVE: 25,
  PRICE_MODIFIER_INACTIVE: 26,
  PRICE_MODIFIER_SCHEDULED: 27,
  PRICE_MODIFIER_ARCHIVED: 39,

  // Add-on statuses
  ADD_ON_AVAILABLE: 28,
  ADD_ON_UNAVAILABLE: 29,
  ADD_ON_DISCONTINUED: 30,

  // Holiday statuses
  HOLIDAY_ACTIVE: 31,
  HOLIDAY_INACTIVE: 32,

  // Price point statuses
  PRICE_POINT_ACTIVE: 33,
  PRICE_POINT_INACTIVE: 34,
  PRICE_POINT_ARCHIVED: 35,

  // Email template statuses
  EMAIL_TEMPLATE_ACTIVE: 36,
  EMAIL_TEMPLATE_DRAFT: 37,
  EMAIL_TEMPLATE_ARCHIVED: 38,

  // Pricing statuses
  PRICING_ACTIVE: 41,
  PRICING_ARCHIVED: 42,

  // FAQ statuses
  FAQ_ACTIVE: 45,
  FAQ_ARCHIVED: 47,

  // Shop item statuses
  SHOP_ITEM_ACTIVE: 48,
  SHOP_ITEM_ARCHIVED: 49,

  // Invoice statuses (for accounting system integration)
  // Note: "Not Generated" status removed - we create tracking record with "Syncing" immediately
  INVOICE_SYNCING: 50,
  INVOICE_CREATION_FAILED: 51,
  INVOICE_SENT_TO_ACCOUNTING: 52,
  INVOICE_PAID: 53,
  INVOICE_DISCONNECTED: 54,

  // Kit statuses
  KIT_ACTIVE: 56,
  KIT_INACTIVE: 57,
  KIT_MAINTENANCE: 58,
} as const;

// Helper to get status type
export function getStatusType(statusId: number): string | null {
  if (statusId >= 1 && statusId <= 3) return "user";
  if (statusId >= 4 && statusId <= 7) return "venue";
  if (statusId >= 8 && statusId <= 11) return "studio";
  if (statusId >= 12 && statusId <= 14) return "show";
  if ((statusId >= 15 && statusId <= 19) || statusId === 43 || statusId === 46)
    return "booking";
  if (statusId >= 20 && statusId <= 24) return "payment";
  if ((statusId >= 25 && statusId <= 27) || statusId === 39)
    return "price_modifier";
  if (statusId >= 28 && statusId <= 30) return "add_on";
  if (statusId >= 31 && statusId <= 32) return "holiday";
  if (statusId >= 33 && statusId <= 35) return "price_point";
  if (statusId >= 36 && statusId <= 38) return "email_template";
  if (statusId >= 41 && statusId <= 42) return "pricing";
  if (statusId === 45 || statusId === 47) return "faq";
  if (statusId >= 48 && statusId <= 49) return "shop_item";
  if (statusId >= 50 && statusId <= 54) return "invoice";
  if (statusId >= 56 && statusId <= 58) return "kit";
  return null;
}

// Price categories
export const PRICE_CATEGORIES = {
  ADDON: "addon",
  SHOPITEM: "shopitem",
  SHOW: "show",
  STUDIO: "studio",
  OTHER: "other",
} as const;

// Payment methods (restricted to only card, cash, invoice as requested)
export const PAYMENT_METHODS = {
  CARD: "card",
  CASH: "cash",
  INVOICE: "invoice",
} as const;

// Helper to get price category display name
export function getPriceCategoryDisplayName(category: string): string {
  const categoryMap: { [key: string]: string } = {
    addon: "Add-on",
    shopitem: "Shop Item",
    show: "Show",
    studio: "Studio",
    other: "Other",
  };
  return categoryMap[category] || category;
}

// Helper to get payment method display name
export function getPaymentMethodDisplayName(method: string): string {
  const methodMap: { [key: string]: string } = {
    card: "Card",
    cash: "Cash",
    invoice: "Invoice",
  };
  return methodMap[method] || method;
}

// Helper to validate payment method
export function isValidPaymentMethod(method: string): boolean {
  return Object.values(PAYMENT_METHODS).includes(method as any);
}

// Status color configuration
export interface StatusColorConfig {
  bgColor: string;
  textColor: string;
  borderColor: string;
}

// Status color themes
const STATUS_THEMES = {
  success: {
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-300",
  },
  danger: {
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    borderColor: "border-red-300",
  },
  warning: {
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-300",
  },
  info: {
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-300",
  },
  neutral: {
    bgColor: "bg-gray-50",
    textColor: "text-gray-700",
    borderColor: "border-gray-300",
  },
  invoiced: {
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    borderColor: "border-purple-300",
  },
  archived: {
    bgColor: "bg-gray-100",
    textColor: "text-gray-600",
    borderColor: "border-gray-400",
  },
  primary: {
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    borderColor: "border-purple-300",
  },
} as const;

// Map status IDs to color themes
const STATUS_ID_COLOR_MAP: Record<number, StatusColorConfig> = {
  // Booking statuses
  [STATUS_IDS.BOOKING_DRAFT]: STATUS_THEMES.warning, // Yellow for draft
  [STATUS_IDS.BOOKING_PRELIMINARY]: STATUS_THEMES.danger, // Red for preliminary
  [STATUS_IDS.BOOKING_CONFIRMED]: STATUS_THEMES.info, // Blue for reserved (confirmed)
  [STATUS_IDS.BOOKING_CANCELLED]: STATUS_THEMES.neutral, // Grey for cancelled
  [STATUS_IDS.BOOKING_COMPLETED]: STATUS_THEMES.success, // Green for completed
  [STATUS_IDS.BOOKING_NO_SHOW]: STATUS_THEMES.primary, // Purple for no show
  [STATUS_IDS.BOOKING_ARCHIVED]: STATUS_THEMES.archived,

  // Payment statuses
  [STATUS_IDS.PAYMENT_PENDING]: STATUS_THEMES.warning,
  [STATUS_IDS.PAYMENT_PAID]: STATUS_THEMES.success,
  [STATUS_IDS.PAYMENT_FAILED]: STATUS_THEMES.danger,
  [STATUS_IDS.PAYMENT_REFUNDED]: STATUS_THEMES.danger,
  [STATUS_IDS.PAYMENT_PARTIAL]: STATUS_THEMES.warning,
  [STATUS_IDS.PAYMENT_INVOICED]: STATUS_THEMES.invoiced, // Purple for invoiced - shows invoice generated but not paid

  // User statuses
  [STATUS_IDS.USER_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.USER_INACTIVE]: STATUS_THEMES.neutral,
  [STATUS_IDS.USER_ARCHIVED]: STATUS_THEMES.archived,

  // Venue statuses
  [STATUS_IDS.VENUE_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.VENUE_INACTIVE]: STATUS_THEMES.neutral,
  [STATUS_IDS.VENUE_MAINTENANCE]: STATUS_THEMES.warning,
  [STATUS_IDS.VENUE_ARCHIVED]: STATUS_THEMES.archived,

  // Studio statuses
  [STATUS_IDS.STUDIO_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.STUDIO_INACTIVE]: STATUS_THEMES.neutral,
  [STATUS_IDS.STUDIO_MAINTENANCE]: STATUS_THEMES.warning,
  [STATUS_IDS.STUDIO_ARCHIVED]: STATUS_THEMES.archived,

  // Show statuses
  [STATUS_IDS.SHOW_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.SHOW_INACTIVE]: STATUS_THEMES.neutral,
  [STATUS_IDS.SHOW_ARCHIVED]: STATUS_THEMES.archived,

  // Kit statuses
  [STATUS_IDS.KIT_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.KIT_INACTIVE]: STATUS_THEMES.neutral,
  [STATUS_IDS.KIT_MAINTENANCE]: STATUS_THEMES.warning,

  // Add-on statuses
  [STATUS_IDS.ADD_ON_AVAILABLE]: STATUS_THEMES.success,
  [STATUS_IDS.ADD_ON_UNAVAILABLE]: STATUS_THEMES.neutral,
  [STATUS_IDS.ADD_ON_DISCONTINUED]: STATUS_THEMES.archived,

  // Holiday statuses
  [STATUS_IDS.HOLIDAY_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.HOLIDAY_INACTIVE]: STATUS_THEMES.neutral,

  // Price point statuses
  [STATUS_IDS.PRICE_POINT_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.PRICE_POINT_INACTIVE]: STATUS_THEMES.neutral,
  [STATUS_IDS.PRICE_POINT_ARCHIVED]: STATUS_THEMES.archived,

  // Email template statuses
  [STATUS_IDS.EMAIL_TEMPLATE_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.EMAIL_TEMPLATE_DRAFT]: STATUS_THEMES.warning,
  [STATUS_IDS.EMAIL_TEMPLATE_ARCHIVED]: STATUS_THEMES.archived,

  // Pricing statuses
  [STATUS_IDS.PRICING_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.PRICING_ARCHIVED]: STATUS_THEMES.archived,

  // Price modifier statuses
  [STATUS_IDS.PRICE_MODIFIER_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.PRICE_MODIFIER_INACTIVE]: STATUS_THEMES.neutral,
  [STATUS_IDS.PRICE_MODIFIER_SCHEDULED]: STATUS_THEMES.info,
  [STATUS_IDS.PRICE_MODIFIER_ARCHIVED]: STATUS_THEMES.archived,

  // FAQ statuses
  [STATUS_IDS.FAQ_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.FAQ_ARCHIVED]: STATUS_THEMES.archived,

  // Shop item statuses
  [STATUS_IDS.SHOP_ITEM_ACTIVE]: STATUS_THEMES.success,
  [STATUS_IDS.SHOP_ITEM_ARCHIVED]: STATUS_THEMES.archived,
};

/**
 * Get color configuration for a status ID
 * @param statusId - The ID of the status from the database
 * @returns StatusColorConfig object with bgColor, textColor, and borderColor
 */
export function getStatusColorConfig(
  statusId: number | null | undefined,
): StatusColorConfig {
  if (!statusId || !STATUS_ID_COLOR_MAP[statusId]) {
    return STATUS_THEMES.neutral;
  }

  return STATUS_ID_COLOR_MAP[statusId];
}

/**
 * Get combined color classes for a status by ID
 * @param statusId - The ID of the status from the database
 * @returns Combined Tailwind CSS classes string
 */
export function getStatusColorClasses(
  statusId: number | null | undefined,
): string {
  const config = getStatusColorConfig(statusId);
  return `${config.bgColor} ${config.textColor} ${config.borderColor}`;
}

/**
 * Get combined color classes for a status by name (for backward compatibility)
 * @param statusName - The name of the status
 * @returns Combined Tailwind CSS classes string
 */
export function getStatusColorClassesByName(
  statusName: string | null | undefined,
): string {
  if (!statusName) {
    return getStatusColorClasses(null);
  }

  // This is a fallback - ideally we should always use status IDs
  const normalizedName = statusName.toLowerCase().trim();

  // Map common status names to themes for backward compatibility
  const nameToTheme: Record<string, StatusColorConfig> = {
    confirmed: STATUS_THEMES.info,
    reserved: STATUS_THEMES.info,
    preliminary: STATUS_THEMES.danger,
    paid: STATUS_THEMES.success,
    cancelled: STATUS_THEMES.neutral,
    refunded: STATUS_THEMES.danger,
    pending: STATUS_THEMES.warning,
    completed: STATUS_THEMES.info,
    archived: STATUS_THEMES.archived,
    active: STATUS_THEMES.success,
    inactive: STATUS_THEMES.neutral,
    "not paid": STATUS_THEMES.warning,
    "invoice sent": STATUS_THEMES.info,
    draft: STATUS_THEMES.warning,
  };

  const theme = nameToTheme[normalizedName] || STATUS_THEMES.neutral;
  return `${theme.bgColor} ${theme.textColor} ${theme.borderColor}`;
}

/**
 * Get status name from status ID for logging and display purposes
 * @param statusId - The ID of the status from the database
 * @returns String name of the status
 */
export function getStatusName(statusId: number | null | undefined): string {
  if (!statusId) return "UNKNOWN";

  // Booking statuses
  if (statusId === STATUS_IDS.BOOKING_PRELIMINARY) return "BOOKING_PRELIMINARY";
  if (statusId === STATUS_IDS.BOOKING_CONFIRMED) return "BOOKING_CONFIRMED";
  if (statusId === STATUS_IDS.BOOKING_CANCELLED) return "BOOKING_CANCELLED";
  if (statusId === STATUS_IDS.BOOKING_COMPLETED) return "BOOKING_COMPLETED";
  if (statusId === STATUS_IDS.BOOKING_NO_SHOW) return "BOOKING_NO_SHOW";
  if (statusId === STATUS_IDS.BOOKING_ARCHIVED) return "BOOKING_ARCHIVED";
  if (statusId === STATUS_IDS.BOOKING_DRAFT) return "BOOKING_DRAFT";

  // Payment statuses
  if (statusId === STATUS_IDS.PAYMENT_PENDING) return "PAYMENT_PENDING";
  if (statusId === STATUS_IDS.PAYMENT_PAID) return "PAYMENT_PAID";
  if (statusId === STATUS_IDS.PAYMENT_FAILED) return "PAYMENT_FAILED";
  if (statusId === STATUS_IDS.PAYMENT_REFUNDED) return "PAYMENT_REFUNDED";
  if (statusId === STATUS_IDS.PAYMENT_PARTIAL) return "PAYMENT_PARTIAL";

  // User statuses
  if (statusId === STATUS_IDS.USER_ACTIVE) return "USER_ACTIVE";
  if (statusId === STATUS_IDS.USER_INACTIVE) return "USER_INACTIVE";
  if (statusId === STATUS_IDS.USER_ARCHIVED) return "USER_ARCHIVED";

  // Add other status types as needed
  return `STATUS_${statusId}`;
}

/**
 * Get display name for booking status
 * @param statusName - The name of the status from the database
 * @param statusId - The ID of the status from the database (optional)
 * @returns Display-friendly status name
 */
export function getBookingStatusDisplay(
  statusName: string | null | undefined,
  statusId?: number | null,
): string {
  // Priority: Use statusId to determine display name
  if (statusId) {
    switch (statusId) {
      case STATUS_IDS.BOOKING_DRAFT:
        return "Draft";
      case STATUS_IDS.BOOKING_PRELIMINARY:
        return "Preliminary";
      case STATUS_IDS.BOOKING_CONFIRMED:
        return "Reserved";
      case STATUS_IDS.BOOKING_CANCELLED:
        return "Cancelled";
      case STATUS_IDS.BOOKING_COMPLETED:
        return "Completed";
      case STATUS_IDS.BOOKING_NO_SHOW:
        return "No Show";
      case STATUS_IDS.BOOKING_ARCHIVED:
        return "Archived";
    }
  }

  // Fallback: Use status name
  if (!statusName) return "Unknown";

  // Normalize the status name
  const normalized = statusName.toLowerCase().trim();

  // Map database status names to display names
  const statusMap: Record<string, string> = {
    draft: "Draft",
    preliminary: "Preliminary",
    confirmed: "Reserved",
    reserved: "Reserved",
    cancelled: "Cancelled",
    completed: "Completed",
    no_show: "No Show",
    "no show": "No Show",
    archived: "Archived",
  };

  return statusMap[normalized] || statusName;
}

/**
 * Get display name for payment status
 * @param statusName - The name of the status from the database
 * @param statusId - The ID of the status from the database (optional)
 * @returns Display-friendly status name
 */
export function getPaymentStatusDisplay(
  statusName: string | null | undefined,
  statusId?: number | null,
): string {
  // Priority: Use statusId to determine display name
  if (statusId) {
    switch (statusId) {
      case STATUS_IDS.PAYMENT_PENDING:
        return "Pending";
      case STATUS_IDS.PAYMENT_PAID:
        return "Paid";
      case STATUS_IDS.PAYMENT_FAILED:
        return "Failed";
      case STATUS_IDS.PAYMENT_REFUNDED:
        return "Refunded";
      case STATUS_IDS.PAYMENT_PARTIAL:
        return "Partial";
      case STATUS_IDS.PAYMENT_INVOICED:
        return "Invoiced";
    }
  }

  // Fallback: Use status name
  if (!statusName) return "Unknown";

  // Normalize the status name
  const normalized = statusName.toLowerCase().trim();

  // Map database status names to display names
  const statusMap: Record<string, string> = {
    pending: "Pending",
    paid: "Paid",
    failed: "Failed",
    refunded: "Refunded",
    partial: "Partial",
    invoiced: "Invoiced",
  };

  return statusMap[normalized] || statusName;
}
